import useApi from "@/hooks/useApi";
import * as React from "react";
import { useEffect, useMemo } from "react";
import { Trigger, WorkflowListItem, triggerDefs } from "./types";
import IllustrationMyWorkflows from "@/icons/illustrations/IllustrationMyWorkflows";
// Chevron imports commented out - collapse functionality disabled
// import IconChevronDown from "@/icons/IconChevronDown";
// import IconChevronRight from "@/icons/IconChevronRight";
import { Checkbox, WorkflowItem } from "./WorkflowHelpers";
import useWorkflows from "@/hooks/useWorkflows";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";

export default function Workflows() {
  const { workflows, setWorkflows, fetch } = useWorkflows();
  // Collapse functionality commented out - only one trigger type currently
  // const [collapsedTriggers, setCollapsedTriggers] = useState<Trigger[]>([]);
  const api = useApi();
  const { displayToast } = useToastAlert();

  useEffect(() => {
    fetch();
  }, [fetch]);

  const groupedWorkflows = useMemo(() => {
    if (!workflows) {
      return {};
    }
    const result: Record<string, WorkflowListItem[]> = {};
    //group based on triggerType property
    workflows.forEach((workflow) => {
      if (!result[workflow.triggerType]) {
        result[workflow.triggerType] = [];
      }
      result[workflow.triggerType].push(workflow);
    });

    return result;
  }, [workflows]);

  const triggers = useMemo(() => {
    if (!workflows) {
      return [];
    }
    return Object.keys(groupedWorkflows) as Trigger[];
  }, [groupedWorkflows, workflows]);

  if (workflows.length === 0) {
    return (
      <div className="p-16 bg-white rounded-2xl border border-slate-200 flex-col justify-center items-center gap-4 inline-flex">
        <div className="flex-col justify-center items-center gap-6 flex">
          <IllustrationMyWorkflows />
          <div className="flex-col justify-center items-center gap-2 flex">
            <div className="text-slate-800 text-lg">Workflows</div>
            <div className="text-center text-slate-500 text-sm">
              Your workflow list is currently empty.
              <br />
              Build your first workflow to enhance customer experiences and
              automate your processes.
            </div>
          </div>
        </div>
      </div>
    );
  }

  async function toggleWorkflow(id: number) {
    if (!workflows) return;

    await api.messaging.post(`Workflows/toggle/${id}`);

    const index = workflows.findIndex((w) => w.id === id);
    const workflow = workflows[index];
    const clone = [...workflows];
    clone[index] = { ...workflow, enabled: !workflow.enabled };
    setWorkflows(clone);
  }

  // Collapse functionality commented out - only one trigger type currently
  // function toggleTrigger(trigger: Trigger) {
  //   if (collapsedTriggers.includes(trigger)) {
  //     setCollapsedTriggers(collapsedTriggers.filter((t) => t !== trigger));
  //   } else {
  //     setCollapsedTriggers([...collapsedTriggers, trigger]);
  //   }
  // }

  async function removeWorkflow(id: number) {
    if (workflows === null) return;

    await api.messaging.delete(`Workflows/${id}`);
    const clone = workflows?.filter((w) => w.id !== id);
    setWorkflows(clone);
  }

  async function duplicateWorkflow(id: number) {
    try {
      const workflowResp = await api.messaging.get(`Workflows/${id}`);
      const payload = await workflowResp.json();

      // POST the duplicated workflow (create minimal record first)
      const createdDuplicatedWFResp = await api.messaging.post("Workflows", {
        trigger: payload.triggerType
      });

      // Try to parse response body to extract created id
      let createdBody: any = null;
      try {
        createdBody = await createdDuplicatedWFResp.json();
      } catch (err) {
        // ignore json parse errors
      }

      // Determine created id from possible shapes
      let createdId: number | undefined;
      if (typeof createdBody === "number") {
        createdId = createdBody;
      } else if (createdBody && typeof createdBody === "object") {
        createdId =
          (createdBody.id as number) ??
          (createdBody.data && (createdBody.data.id as number)) ??
          (createdBody.workflowId as number) ??
          undefined;
      }

      // If no id in body, try Location header
      if (
        !createdId &&
        createdDuplicatedWFResp &&
        (createdDuplicatedWFResp as any).headers
      ) {
        try {
          const location = (createdDuplicatedWFResp as any).headers.get
            ? (createdDuplicatedWFResp as any).headers.get("location") ||
              (createdDuplicatedWFResp as any).headers.get("Location")
            : null;
          if (location) {
            const m = location.match(/\/(\d+)(?:\D*$)/);
            if (m) createdId = parseInt(m[1], 10);
          }
        } catch (_e) {
          // ignore
        }
      }

      if (!createdId) {
        // If we couldn't find an id, refresh the list and exit
        await fetch();
        return;
      }

      // Now update the created workflow with the full details (name, nodes, etc.)
      await api.messaging.put("Workflows", {
        id: createdId,
        name: `${payload.name} - Copy`,
        nodes: payload.nodes,
        triggerType: payload.triggerType
      });

      // Refresh list so the UI shows the newly created workflow
      await fetch();

      // Inform the user
      displayToast(Severity.Success, `Workflow duplicated: ${payload.name}`);
    } catch (e) {
      console.error("Failed to duplicate workflow", e);
      try {
        displayToast(Severity.Error, "Failed to duplicate workflow");
      } catch (_err) {
        // ignore
      }
    }
  }

  function renderTrigger(trigger: Trigger, index: number) {
    // Collapse functionality commented out - only one trigger type currently
    // const isCollapsed = collapsedTriggers.includes(trigger);
    return (
      <div className="p-4" key={index}>
        <div className="mb-4 flex">
          <div className="flex gap-2 items-center font-bold">
            {/* Chevron icons commented out - only one trigger type currently */}
            {/* {isCollapsed ? <IconChevronRight /> : <IconChevronDown />} */}
            {triggerDefs[trigger]}
          </div>
        </div>
        {/* Table always visible - collapse functionality commented out */}
        {/* {!isCollapsed && ( */}
          <div className="bg-white border rounded-lg border-slate-200">
            <table cellPadding={10} className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-200 h-12">
                  <th className="w-1/12">
                    <div className="flex items-center">
                      <Checkbox />
                    </div>
                  </th>
                  <th className="w-1/4">Name</th>
                  <th className="w-1/4">Created by</th>
                  <th className="w-1/6">Updated</th>
                  <th className="w-1/6">Status</th>
                  <th className="w-1/12">Action</th>
                </tr>
              </thead>
              <tbody>
                {groupedWorkflows[trigger].map((workflow, i) => (
                  <WorkflowItem
                    key={i}
                    item={workflow}
                    isLast={i === groupedWorkflows[trigger].length - 1}
                    toggle={() => toggleWorkflow(workflow.id)}
                    remove={() => removeWorkflow(workflow.id)}
                    duplicate={() => duplicateWorkflow(workflow.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        {/* )} */}
      </div>
    );
  }

  return <>{triggers.map(renderTrigger)}</>;
}
